import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import MockAdDialog from "../components/MockAdDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import DailyWorkoutCard from "../components/DailyWorkoutCard";
import StepsCard from "../components/StepsCard";
import WelcomeCarousel, { hasSeenWelcome } from "../components/WelcomeCarousel";
import ShareCard from "../components/ShareCard";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Flame, Coins, PlayCircle, Gift, Footprints, X, HelpCircle, ChevronRight, Infinity as InfinityIcon, Timer, Trophy } from "lucide-react";
import { toast } from "sonner";
import { sfx } from "../lib/sound";
import { motion } from "framer-motion";
import { isHealthSupported, syncStepsToBackend, autoSyncStepsIfDue, openHealthConnect } from "../lib/health";
import { useStepsUiEnabled } from "../lib/stepsOptIn";
import { preloadRewarded, adDiag } from "../lib/admob";
import { ensureDailyResetReminder } from "../lib/notifications";

const ENDURANCE_BG = "https://images.unsplash.com/photo-1770698693897-ee89c1f7aea2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxydW5uaW5nJTIwdHJhY2slMjBuaWdodCUyMHN0YWRpdW18ZW58MHx8fHwxNzg1MzE5MjQ3fDA&ixlib=rb-4.1.0&q=85";
import { translateLevelName } from "../lib/journeyLabels";
import { cacheJourneyCatalog, cacheModeBoard, flushQueue } from "../lib/offlineQueue";
import HeroStreak from "../components/HeroStreak";
import PerkModal from "../components/PerkModal";
import WorldUnlockCelebration from "../components/WorldUnlockCelebration";
import DailyGoalClaimCelebration from "../components/DailyGoalClaimCelebration";
import CoinBurst from "../components/CoinBurst";
import StatStrip from "../components/StatStrip";
import MilestoneChip from "../components/MilestoneChip";
import { useTranslation } from "react-i18next";

/**
 * Iter49 — Resolve the "refills earned today" badge value for the
 * Steps card. Returns `health.energy_awarded_today` ONLY when the
 * cached `last_sync_date` matches the user's local "today", so the
 * counter visually rolls back to 0/10 at midnight without needing a
 * fresh sync. Hoisted out of JSX so it doesn't trigger the React-19
 * react-hooks/purity rule (Date.now() in render).
 */
function dailyRefillsForDisplay(health) {
  if (!health) return 0;
  const lastDate = health.last_sync_date;
  if (!lastDate) return 0;
  // Compare in LOCAL time (matches backend `_local_today` logic).
  const offsetMin = new Date().getTimezoneOffset();
  const localToday = new Date(Date.now() - offsetMin * 60 * 1000)
    .toISOString().slice(0, 10);
  return lastDate === localToday ? Number(health.energy_awarded_today || 0) : 0;
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, profile, setProfile, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [journey, setJourney] = useState(null);
  const [dailyGoal, setDailyGoal] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [dailyWorkout, setDailyWorkout] = useState(null);
  const [energyAd, setEnergyAd] = useState(false);
  const [streakAd, setStreakAd] = useState(false);
  const [streakDialog, setStreakDialog] = useState(false);
  const [streakCoinPending, setStreakCoinPending] = useState(false);
  const [energyDialog, setEnergyDialog] = useState(false);
  const [coinRefillPending, setCoinRefillPending] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  // Show welcome carousel exactly once per device for new users (first 3 days
  // since signup, no completed levels yet, and not previously dismissed).
  useEffect(() => {
    if (!profile) return;
    if (hasSeenWelcome()) return;
    const isNew = (profile.completed_levels?.length || 0) === 0 && (profile.total_games || 0) === 0;
    if (isNew) setShowWelcome(true);
  }, [profile?.user_id]);
  const [syncingSteps, setSyncingSteps] = useState(false);
  // Daily Challenge "available today" banner — visible only when the user
  // hasn't cleared today's seeded board yet. Tester request v34b: surface
  // the Daily Challenge hook from the Dashboard instead of burying it in
  // the More menu — it's the strongest daily-return loop we have.
  const [dailyChallenge, setDailyChallenge] = useState(null);
  // Endurance home CTA — /endurance/info: {unlocked, journey_completed,
  // journey_total_levels, miles, best_time_sec}.
  const [endurance, setEndurance] = useState(null);
  // Sequential perk celebration queue (welcome-back, avatar/yogi perk,
  // weekly-champion reward) — shown ONE AT A TIME via <PerkModal/>.
  const [perkQueue, setPerkQueue] = useState([]);
  const [syncUnlock, setSyncUnlock] = useState(null); // world unlocked during offline sync
  const [goalClaim, setGoalClaim] = useState(null); // {coins, boosters} after claiming daily goal
  const [coinBurst, setCoinBurst] = useState(0); // trigger for the flying-coins HUD animation
  const pendingProfileRef = useRef(null); // holds the post-claim profile until the celebration closes
  // Enforce the first-open card order regardless of which effect fires
  // first: Welcome-back (sort 1) → Yogi/Coach perk (sort 2) → Weekly
  // Winners (sort 3). Full-sort is safe because welcome-back (the lowest
  // priority number) enqueues synchronously on mount, so no lower-priority
  // card ever arrives late to displace a card that's already showing.
  const enqueuePerk = (perk) =>
    setPerkQueue((q) =>
      q.some((p) => p.key === perk.key)
        ? q
        : [...q, perk].sort((a, b) => (a.sort ?? 99) - (b.sort ?? 99)),
    );
  // First-run "Connect Google Fit" banner — auto-dismissed once the user
  // either successfully syncs (profile.health.last_steps > 0) or taps the X.
  const [healthBannerDismissed, setHealthBannerDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("fitjong_health_banner_dismissed") === "1",
  );
  const stepsOn = useStepsUiEnabled();
  const showHealthBanner =
    isHealthSupported() &&
    stepsOn &&
    !healthBannerDismissed &&
    !Number(profile?.health?.last_steps || 0);
  const dismissHealthBanner = () => {
    setHealthBannerDismissed(true);
    try { localStorage.setItem("fitjong_health_banner_dismissed", "1"); } catch (_) {}
  };
  const [showHealthHelp, setShowHealthHelp] = useState(false);
  const loadAll = async () => {
    try {
      const [j, d, m, dw, dc, en] = await Promise.all([
        api.get("/journey"), api.get("/daily-goal"), api.get("/streak/milestones"),
        api.get("/daily-workout"), api.get("/daily-challenge/today"),
        api.get("/endurance/info").catch(() => ({ data: null })),
      ]);
      setJourney(j.data); setDailyGoal(d.data); setMilestones(m.data); setDailyWorkout(dw.data);
      setDailyChallenge(dc.data);
      if (en.data) setEndurance(en.data);
      // Offline Play — cache board metadata so games can be generated with
      // no connectivity.
      cacheJourneyCatalog(j.data.worlds);
      if (dc.data?.layout) cacheModeBoard("daily", dc.data.layout, dc.data.target_time_sec);
    } catch {}
  };

  // Offline Play — flush any queued offline results when the Dashboard loads
  // and whenever connectivity returns.
  useEffect(() => {
    const doFlush = () => flushQueue().then((r) => {
      if (r.ok && r.profile) setProfile(r.profile);
      // Iter117 — confirm offline games counted (toast) + replay the world
      // unlock moment if a world opened while the player was offline.
      if (r.ok && r.synced > 0) {
        const coins = Number(r.coinsSynced || 0);
        toast.success(
          coins > 0
            ? t("offline.synced_with_coins", { n: r.synced, coins })
            : t("offline.synced", { n: r.synced }),
          { duration: 4000 }
        );
      }
      if (r.ok && r.unlockedWorld) setSyncUnlock(r.unlockedWorld);
    }).catch(() => {});
    doFlush();
    window.addEventListener("online", doFlush);
    return () => window.removeEventListener("online", doFlush);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [profile?.completed_levels?.length, profile?.current_streak, profile?.daily_goal_progress]);

  // Auto-open energy refill dialog when arriving from Play's out-of-energy
  // error screen via /dashboard?refill=1. Drops the query param after
  // consuming it so a refresh doesn't re-open the dialog.
  useEffect(() => {
    if (searchParams.get("refill") === "1") {
      setEnergyDialog(true);
      const next = new URLSearchParams(searchParams);
      next.delete("refill");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-sync steps in the background on Dashboard mount. Fire-and-forget
  // so a slow Health Connect read can't block the page from rendering.
  useEffect(() => {
    let cancelled = false;
    autoSyncStepsIfDue().then((result) => {
      if (cancelled) return;
      if (result?.ok && (result.energy_granted || 0) > 0) {
        setProfile(result.profile);
        toast.success(t("dashboard.toast_steps_to_energy", { energy: result.energy_granted, steps: result.steps.toLocaleString() }));
      } else if (result?.ok) {
        setProfile(result.profile);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background pre-load of a rewarded ad so the "Watch ad" button in the
  // energy refill dialog opens the ad within ~200ms instead of 2-5s.
  // Tester complaint v33: "Ad play with a huge delay". We retry once a
  // minute in case the first load failed (no fill, network blip).
  useEffect(() => {
    if (!user?.user_id) return;
    preloadRewarded({ userId: user.user_id, rewardType: "energy" });
    const id = setInterval(() => {
      preloadRewarded({ userId: user.user_id, rewardType: "energy" });
    }, 60_000);
    return () => clearInterval(id);
  }, [user?.user_id]);

  // Also pre-warm right before the dialog opens — covers the case where
  // the initial preload hadn't completed yet by the time the user taps the
  // energy card.
  useEffect(() => {
    if (energyDialog && user?.user_id) {
      preloadRewarded({ userId: user.user_id, rewardType: "energy" });
    }
  }, [energyDialog, user?.user_id]);

  // Daily Challenge reminder — local 09:00 notification. Re-schedules
  // every time the i18n language changes so the body text matches the
  // user's CURRENT language preference (tester v38: "notification came up
  // in Polish, should be in English" — the schedule was created when the
  // app first launched in Polish and never updated when the user later
  // switched to English). Idempotent at the OS level.
  const lang = i18n.language;
  // Iter54 — when the user has ALREADY completed today's daily
  // challenge, skip TODAY's 09:00 reminder (push first fire to
  // tomorrow). The daily schedule then continues normally.
  const dailyChallengeDoneToday = (() => {
    const dates = profile?.daily_challenge_dates || [];
    if (!dates.length) return false;
    const offsetMin = new Date().getTimezoneOffset();
    const localToday = new Date(Date.now() - offsetMin * 60 * 1000)
      .toISOString().slice(0, 10);
    return dates.includes(localToday);
  })();
  useEffect(() => {
    ensureDailyResetReminder({
      title: t("notifications.daily_reset_title"),
      body: t("notifications.daily_reset_body"),
      skipToday: dailyChallengeDoneToday,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, dailyChallengeDoneToday]);

  // Iter59 — when the backend signals that today's avatar perk just
  // refreshed, toast the user so they see WHERE the extra Coach Tip
  // came from (tester confusion: "had 0 hints in quick session, 1 in
  // daily workout — didn't know why"). One toast per day.
  useEffect(() => {
    if (!profile?.avatar_perk_fresh) return;
    const perkKey = profile.avatar_perk_key;
    const shownKey = "fitjong:avatarPerkToastShown";
    if (typeof localStorage !== "undefined" && localStorage.getItem(shownKey) === perkKey) return;
    if (profile.selected_avatar === "yogi") {
      enqueuePerk({
        key: "yogi:" + perkKey,
        type: "coachtip",
        sort: 2,
        title: t("perks.yogi_title", "Yogi Perk Unlocked"),
        subtitle: t("perks.yogi_sub", "+1 Coach Tip for today — boosters carry across every game mode."),
      });
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(shownKey, perkKey);
    }
  }, [profile?.avatar_perk_fresh, profile?.avatar_perk_key, profile?.selected_avatar, t]);

  // Welcome-back greeting — the FIRST card shown once per calendar day when
  // the player opens the app (before any perk card). Shows regardless of
  // streak value: returning players who missed days still get greeted, with
  // comeback copy instead of a streak count. Only brand-new players skip it.
  useEffect(() => {
    if (!profile) return;
    if ((profile.total_games || 0) === 0) return; // brand-new player → skip
    const streak = Number(profile.current_streak || 0);
    const today = new Date().toISOString().slice(0, 10);
    const shownKey = "fitjong:welcomeBackShown";
    if (typeof localStorage !== "undefined" && localStorage.getItem(shownKey) === today) return;
    enqueuePerk({
      key: "welcomeback:" + today,
      type: "streak",
      sort: 1,
      autoCloseMs: 3600,
      title: t("perks.welcome_title", "Welcome back, {{name}}!", { name: profile.display_name || t("common.athlete", "Athlete") }),
      subtitle: streak >= 1
        ? t("perks.welcome_sub", "You're on a {{n}}-day streak — keep the momentum going.", { n: streak })
        : t("perks.welcome_comeback", "Ready to start a fresh streak? Play today to get rolling."),
    });
    if (typeof localStorage !== "undefined") localStorage.setItem(shownKey, today);
  }, [profile?.user_id, profile?.current_streak, t]);

  // Iter56 — Monday-rollover Weekly Champion payout toast. Pulls
  // `pending_weekly_payouts` from the backend, displays a celebratory
  // toast for each entry, and refreshes the profile so the user sees
  // their updated coin balance. Tester complaint: "weekly challenge
  // ... coins didn't add from weekly challenge, should be +100".
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/weekly-tournament/claim-payouts");
        if (cancelled) return;
        const payouts = data?.payouts || [];
        if (payouts.length === 0) return;
        for (const p of payouts) {
          const key = "weeklypayout:" + (p.week || "") + ":" + p.rank;
          const titleKey = p.rank === 1
            ? "perks.weekly_rank1" : p.rank === 2 ? "perks.weekly_rank2" : "perks.weekly_rank3";
          const titleFallback = p.rank === 1
            ? "Weekly Champion!" : p.rank === 2 ? "2nd Place Last Week" : "3rd Place Last Week";
          enqueuePerk({
            key,
            type: "champion",
            sort: 3,
            title: t(titleKey, titleFallback),
            subtitle: t("perks.weekly_sub", "+{{coins}} coins landed in your wallet.", { coins: p.bonus_coins }),
          });
        }
        // Refresh so the new coin balance shows on the dashboard.
        if (refresh) await refresh();
        // Fly coins into the HUD to make the payout feel "collected".
        setCoinBurst((n) => n + 1);
      } catch (e) {
        // Silent — non-critical.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, refresh, t]);

  // Daily podium payout — pulls `pending_daily_payouts` for yesterday's
  // top-3 finishers, shows a celebration card per entry, and refreshes the
  // coin balance. Mirrors the Weekly Champion payout above.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/daily-challenge/claim-payouts");
        if (cancelled) return;
        const payouts = data?.payouts || [];
        if (payouts.length === 0) return;
        for (const p of payouts) {
          const key = "dailypayout:" + (p.date || "") + ":" + p.rank;
          const titleKey = p.rank === 1
            ? "perks.daily_rank1" : p.rank === 2 ? "perks.daily_rank2" : "perks.daily_rank3";
          const titleFallback = p.rank === 1
            ? "Yesterday's Champion!" : p.rank === 2 ? "2nd on Yesterday's Board" : "3rd on Yesterday's Board";
          enqueuePerk({
            key,
            type: "champion",
            sort: 4,
            title: t(titleKey, titleFallback),
            subtitle: t("perks.daily_sub", "+{{coins}} coins landed in your wallet.", { coins: p.bonus_coins }),
          });
        }
        if (refresh) await refresh();
        setCoinBurst((n) => n + 1);
      } catch (e) {
        // Silent — non-critical.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, refresh, t]);


  if (!user || !profile) return <p className="text-[#2C332A]/60">{t("common.loading")}</p>;

  const nextLevel = (() => {
    if (!journey) return null;
    for (const w of journey.worlds) {
      const lvl = w.levels.find((l) => l.unlocked && !l.completed);
      if (lvl) return { world: w, level: lvl };
    }
    return null;
  })();

  // Slowest completed level — the single level with the most room to shave
  // time. Powers the "Beat your best" prompt so the player knows exactly
  // where to focus once the Journey is 100% done.
  const slowestLevel = (() => {
    if (!journey) return null;
    let worst = null;
    for (const w of journey.worlds) {
      for (const l of w.levels) {
        if (l.completed && l.best_time_sec != null) {
          if (!worst || l.best_time_sec > worst.level.best_time_sec) worst = { world: w, level: l };
        }
      }
    }
    return worst;
  })();
  const fmtSec = (s) => { const n = Math.max(0, Math.round(Number(s) || 0)); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`; };

  // The native AdMob path was previously assuming Google's SSV callback would
  // grant the reward on the backend. In practice the SSV postback URL has to
  // be wired in the AdMob console — until that's done the backend is never
  // pinged. Always call the refill endpoint client-side; it's idempotent
  // (sets energy = max_energy) so calling it twice does no harm.
  //
  // Iter56 — Tester explicitly requested visible confirmation after a
  // rewarded ad finishes ("Energy added" on success / "Could not refill
  // energy, please try again" on failure) AND a single automatic retry
  // 1 second after a failure. The retry catches the common case where
  // the device has a transient network blip right when the ad SDK
  // releases its fullscreen overlay — by the time the retry fires, the
  // connection is usually back.
  const refillEnergy = async () => {
    adDiag("refillEnergy RAN → calling /energy/refill");
    toast.info("⚡ Refilling energy…", { duration: 3500 });
    const callOnce = () => api.post("/energy/refill");
    try {
      const { data } = await callOnce();
      setProfile(data);
      toast.success(t("dashboard.toast_energy_added", { current: data.energy, max: data.max_energy }), { duration: 3500 });
      return;
    } catch (e1) {
      const code1 = e1?.response?.status || "net";
      toast.info(`Refill retry (1/1) · code=${code1}`, { duration: 3500 });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const { data } = await callOnce();
        setProfile(data);
        toast.success(t("dashboard.toast_energy_added", { current: data.energy, max: data.max_energy }), { duration: 3500 });
        return;
      } catch (e2) {
        const code2 = e2?.response?.status || "net";
        toast.error(`${t("dashboard.toast_could_not_refill")} (codes ${code1}/${code2})`, { duration: 3500 });
      }
    }
  };

  // Coin sink: spend 25 coins for +1 energy or 100 for +5 (animated tick-up
  // — calls the backend once but pulses the toast / sfx N times for feel).
  const refillEnergyWithCoins = async (amount) => {
    if (coinRefillPending) return;
    setCoinRefillPending(true);
    try {
      const { data } = await api.post("/energy/refill-coins", { amount });
      const granted = data.energy_granted || amount;
      // Optimistic visual tick-up — wait between pulses so the user sees the
      // counter rise one at a time before the final profile update lands.
      const start = profile.energy;
      for (let i = 1; i <= granted; i++) {
        sfx.coinCash?.();
        setProfile((prev) => prev ? { ...prev, energy: Math.min(prev.max_energy, start + i) } : prev);
        if (i < granted) await new Promise((r) => setTimeout(r, 220));
      }
      // Apply authoritative server profile last (coins + energy)
      setProfile(data.profile);
      toast.success(t("dashboard.toast_energy_coins_spent", { energy: granted, coins: data.coins_spent }));
      setEnergyDialog(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashboard.toast_could_not_spend"));
    } finally {
      setCoinRefillPending(false);
    }
  };
  const restoreStreak = async () => {
    try {
      const { data } = await api.post("/streak/restore");
      setProfile(data.profile || data);
      if (data.restored === false) {
        toast(t("dashboard.toast_streak_already_safe", "Your streak is already safe today!"));
      } else {
        toast.success(t("dashboard.toast_streak_restored"));
        setCoinBurst((n) => n + 1);
      }
    } catch {
      toast.error(t("dashboard.toast_could_not_restore_streak"));
    }
  };
  const restoreStreakWithCoins = async () => {
    if (streakCoinPending) return;
    setStreakCoinPending(true);
    try {
      const { data } = await api.post("/streak/restore-coins");
      setProfile(data.profile || data);
      toast.success(t("dashboard.toast_streak_kept_coins", "Streak kept! −{{coins}} coins", { coins: data.coins_spent }));
      setStreakDialog(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashboard.toast_could_not_restore_streak"));
    } finally {
      setStreakCoinPending(false);
    }
  };
  const claimDailyGoal = async () => {
    try {
      const { data } = await api.post("/daily-goal/claim");
      // Hold the new profile (incl. the higher coin balance) until the
      // celebration closes, so the HUD count-up + coin-burst play together
      // on a clear screen instead of jumping instantly behind the card.
      pendingProfileRef.current = data.profile;
      setGoalClaim({ coins: data.reward_coins || 0, boosters: data.reward_boosters || {} });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dashboard.toast_cannot_claim")); }
  };
  const claimMilestone = async (day) => {
    try {
      const { data } = await api.post(`/streak/milestone/claim/${day}`);
      setProfile(data.profile);
      sfx.coinCash();
      toast.success(t("dashboard.toast_milestone_unlocked", { coins: data.milestone.reward_coins })); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dashboard.toast_cannot_claim")); }
  };

  const syncSteps = async () => {
    if (syncingSteps) return;
    setSyncingSteps(true);
    try {
      const res = await syncStepsToBackend();
      if (!res.ok) {
        const msg = {
          web: t("dashboard.health_sync_web"),
          "no-permission": t("dashboard.health_sync_no_permission"),
          "not-installed": t("dashboard.health_sync_not_installed"),
          "plugin-not-linked": t("dashboard.health_sync_plugin_missing"),
          timeout: t("dashboard.health_sync_timeout"),
          error: res.detail || res.error || t("dashboard.health_sync_failed"),
        }[res.reason] || `${t("dashboard.health_sync_failed")} (${res.reason})`;
        toast.error(msg, {
          duration: 6000,
          action: ["no-permission", "not-installed", "timeout", "plugin-not-linked"].includes(res.reason)
            ? { label: t("common.help"), onClick: () => setShowHealthHelp(true) }
            : undefined,
        });
        return;
      }
      if (res.profile) setProfile(res.profile);
      if (res.energy_granted > 0) {
        toast.success(t("dashboard.toast_steps_today_energy", { energy: res.energy_granted, steps: res.steps.toLocaleString() }));
      } else if (res.profile && res.profile.energy >= (res.profile.max_energy || 10)) {
        // Energy is already at max → not a "keep walking" situation; the
        // user just doesn't have headroom right now. Friendlier message
        // than the generic "walk more" copy.
        toast.success(t("dashboard.toast_steps_today_full", { steps: res.steps.toLocaleString() }));
      } else if (res.steps < 8000) {
        // Iter40 fix per senior-support review: the previous fall-through
        // showed the "daily energy cap reached" toast even when the user
        // hadn't yet hit the 8,000-step first threshold. That was
        // confusing — they'd see "cap reached" at 3,000 steps. Show the
        // "keep walking" message instead when below the first reward.
        toast.success(t("dashboard.toast_steps_today_keep", { steps: res.steps.toLocaleString() }));
      } else {
        // Steps ≥ 8,000 AND no energy granted AND headroom present →
        // user already collected today's per-day step-energy quota.
        toast.success(t("dashboard.toast_steps_today_capped", { steps: res.steps.toLocaleString() }));
      }
    } finally {
      setSyncingSteps(false);
    }
  };

  return (
    <div className="space-y-3 max-w-xl mx-auto pb-6">
      <HeroStreak
        name={(profile?.display_name || user?.name || user?.email || t("hud.player")).split(" ")[0]}
        streak={profile.current_streak}
        hasNext={!!nextLevel}
        continueLabel={nextLevel ? t("dashboard.continue_level", { name: translateLevelName(nextLevel.level, t) }) : t("dashboard.view_journey")}
        onContinue={() => (nextLevel
          ? navigate("/journey", { state: { focus: { world_id: nextLevel.world.world_id, level_id: nextLevel.level.level_id } } })
          : navigate("/journey"))}
        onQuick={() => navigate("/play/quick/quick")}
        endurance={endurance}
        onEndurance={() => navigate("/endurance")}
      />

      {endurance?.unlocked && (
        <button
          data-testid="dashboard-endurance-banner"
          onClick={() => navigate("/endurance")}
          className="relative w-full overflow-hidden rounded-2xl text-left active:scale-[0.98] transition-transform"
          style={{ background: "#1A1F19" }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.18] mix-blend-overlay"
            style={{ backgroundImage: `url(${ENDURANCE_BG})` }}
            aria-hidden="true"
          />
          <span
            aria-hidden="true"
            className="absolute -top-3 right-2 font-black italic tracking-tighter text-white/[0.05] select-none pointer-events-none"
            style={{ fontFamily: "Outfit, sans-serif", fontSize: "3.5rem", lineHeight: 1 }}
          >
            ∞
          </span>
          <div className="relative z-10 p-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#E8A365]/15 grid place-items-center shrink-0">
                <InfinityIcon size={20} strokeWidth={2} className="text-[#E8A365]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] uppercase tracking-[0.22em] text-[#E8A365] font-bold">
                  {t("endurance.banner_label", "Run Forever")}
                </p>
                <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-base font-black tracking-tight text-white leading-tight mt-0.5">
                  {t("endurance.banner_miles", { n: endurance.miles || 0 })}
                </p>
              </div>
            </div>
            <span className="mt-3 flex w-full items-center justify-center gap-1.5 h-10 rounded-full bg-[#E8A365] text-[#1A1F19] font-bold text-sm px-4">
              <Timer size={15} strokeWidth={2.2} />
              {(endurance.miles || 0) > 0 ? t("endurance.banner_cta", "Continue run") : t("endurance.banner_cta_start", "Start running")}
            </span>
          </div>
        </button>
      )}

      {journey && journey.progress_pct >= 100 && (
        <motion.div
          className="rounded-3xl"
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(232,163,101,0.0)",
              "0 0 34px 9px rgba(232,163,101,0.75)",
              "0 0 0 0 rgba(232,163,101,0.0)",
            ],
          }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Link
            to={slowestLevel ? `/play/${slowestLevel.world.world_id}/${slowestLevel.level.level_id}` : "/journey"}
            data-testid="dashboard-beat-your-best"
            className="relative block overflow-hidden rounded-3xl p-4 active:scale-[0.99] transition-transform"
            style={{ background: "linear-gradient(135deg,#E8A365 0%,#D9924E 55%,#C97F3C 100%)" }}
          >
            <Trophy size={72} strokeWidth={1.4} className="absolute -right-3 -bottom-4 text-white/15" aria-hidden="true" />
            <div className="relative flex items-center gap-3">
              <motion.div
                className="w-11 h-11 rounded-2xl bg-white/25 grid place-items-center shrink-0"
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <Trophy size={22} strokeWidth={1.8} className="text-white" />
              </motion.div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold">
                  {t("daily_goal.journey_complete_label", "Journey complete")}
                </p>
                <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-base font-bold text-white leading-tight">
                  {t("daily_goal.beat_your_best_title", "Beat your best times")}
                </p>
                {slowestLevel ? (
                  <p className="text-xs text-white/90 mt-0.5 leading-snug" data-testid="beat-your-best-target">
                    {t("daily_goal.beat_your_best_slowest", "Slowest: {{name}} · {{time}} — shave it down for 3★", {
                      name: translateLevelName(slowestLevel.level, t),
                      time: fmtSec(slowestLevel.level.best_time_sec),
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-white/85 mt-0.5 leading-snug">
                    {t("daily_goal.beat_your_best_sub", "Replay any level faster to earn 3★ and burn more calories.")}
                  </p>
                )}
              </div>
              <ChevronRight size={20} strokeWidth={2} className="text-white/80 shrink-0" />
            </div>
          </Link>
        </motion.div>
      )}

      {showHealthBanner && (
        <motion.div
          data-testid="health-onboarding-banner"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-4 md:p-5 border border-[#5F7161]/30 bg-gradient-to-r from-[#EFF1EC] to-[#F7F5F0] shadow-sm relative"
        >
          <button
            data-testid="health-banner-dismiss"
            onClick={dismissHealthBanner}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/60 text-[#2C332A]/50 hover:text-[#2C332A] transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} strokeWidth={2} />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div className="w-10 h-10 rounded-full bg-[#5F7161] grid place-items-center shrink-0 shadow-sm">
              <Footprints size={20} strokeWidth={1.8} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-base md:text-lg font-medium text-[#2C332A] leading-tight"
              >
                {t("dashboard.health_banner_title")}
              </h3>
              <p className="text-sm text-[#2C332A]/70 mt-1 leading-snug">
                {t("dashboard.health_banner_body")}
              </p>
              <Button
                data-testid="health-banner-connect-btn"
                onClick={syncSteps}
                disabled={syncingSteps}
                className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-9 px-4 text-sm mt-3"
              >
                <Footprints size={14} strokeWidth={1.8} className="mr-2" />
                {syncingSteps ? t("dashboard.connecting") : t("dashboard.connect_health")}
              </Button>
              <button
                data-testid="health-banner-help-btn"
                onClick={() => setShowHealthHelp(true)}
                className="ml-2 text-xs text-[#5F7161] underline underline-offset-2 hover:text-[#4D5D4F]"
              >
                {t("dashboard.how_does_this_work")}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <Dialog open={showHealthHelp} onOpenChange={setShowHealthHelp}>
        <DialogContent
          data-testid="health-help-dialog"
          className="rounded-3xl border-[#E6E2D8] max-w-[360px] sm:max-w-md p-5 max-h-[80vh] overflow-y-auto"
        >
          <DialogHeader className="space-y-1.5">
            <DialogTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-xl font-light flex items-center gap-2"
            >
              <HelpCircle size={20} strokeWidth={1.5} className="text-[#5F7161]" />
              {t("dashboard.health_help_title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#2C332A]/70 leading-relaxed">
              {t("dashboard.health_help_intro")}
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-[#2C332A] mt-3 list-decimal pl-4 leading-relaxed">
            <li>{t("dashboard.health_help_step1")}</li>
            <li>{t("dashboard.health_help_step2")}</li>
            <li>{t("dashboard.health_help_step3")}</li>
            <li>{t("dashboard.health_help_step4")}</li>
            <li>{t("dashboard.health_help_step5")}</li>
            <li>{t("dashboard.health_help_step6")}</li>
          </ol>
          <div className="bg-[#F0EDE3] rounded-xl p-3 mt-4 text-xs text-[#2C332A]/80 leading-relaxed">
            <p className="font-semibold mb-1">{t("dashboard.health_help_formula_title")}</p>
            <p>{t("dashboard.health_help_formula1")}</p>
            <p>{t("dashboard.health_help_formula2")}</p>
            <p>{t("dashboard.health_help_formula3")}</p>
          </div>
          <button
            data-testid="health-help-open-hc"
            onClick={() => openHealthConnect()}
            className="mt-4 w-full rounded-full bg-[#E8A365] hover:bg-[#D38F4F] text-white py-2.5 text-sm font-medium"
          >
            {t("dashboard.open_health_connect")}
          </button>
          <button
            data-testid="health-help-close"
            onClick={() => setShowHealthHelp(false)}
            className="mt-2 w-full rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white py-2.5 text-sm font-medium"
          >
            {t("common.got_it")}
          </button>
        </DialogContent>
      </Dialog>
      <StatStrip
        profile={profile}
        goal={dailyGoal}
        onEnergy={() => setEnergyDialog(true)}
        onStreak={() => setStreakDialog(true)}
        onClaimGoal={claimDailyGoal}
      />

      {/* Steps card — only shown on Android (Health Connect platform).
          Tester request v42: "add a 'Last synced' timestamp, a manual
          Refresh button, and a tooltip explaining steps come from Health
          Connect". Lives below the 3-card grid so it doesn't shove the
          Streak / Energy cards off a narrow phone, but stays above the
          Daily-Challenge banner for visibility. */}
      {isHealthSupported() && stepsOn && (
        <StepsCard
          steps={Number(profile.health?.last_steps || 0)}
          lastSyncAt={profile.health?.last_sync_at}
          syncing={syncingSteps}
          onRefresh={() => syncSteps()}
          onHelp={() => setShowHealthHelp(true)}
          /* Iter49 — hybrid clarity: surface daily energy-refill counter
             so steps clearly read as a real-world measurement, not a
             currency that gets spent on refills. Resets visually when
             today's local-date differs from last_sync_date (so the badge
             rolls to 0/10 at midnight before the next sync arrives). */
          refillsEarnedToday={dailyRefillsForDisplay(profile.health)}
          refillsCap={10}
        />
      )}

      {dailyWorkout && <DailyWorkoutCard data={dailyWorkout} />}

      {milestones && milestones.milestones.length > 0 && (
        <div className="p-3 rounded-2xl bg-white border border-[#E6E2D8]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8AA48F] font-bold mb-2">
            {t("daily_goal.milestones_title")}
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {milestones.milestones.map((m) => (
              <MilestoneChip key={m.day} m={m} onClaim={claimMilestone} />
            ))}
          </div>
        </div>
      )}

      {/* Calories footer — the "real calories burned" payoff, plus games,
          focus time and overall journey progress. Mirrors the store-listing
          hero: bold flame + big kcal number on a deep-ink card. */}
      <div
        data-testid="calories-footer"
        className="rounded-3xl p-4 text-white"
        style={{ background: "linear-gradient(160deg,#2C332A 0%,#26302A 100%)" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#E8A365]/20 grid place-items-center shrink-0">
            <Flame size={24} className="text-[#E8A365]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-end gap-1.5 leading-none">
              <span
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-3xl font-black text-[#E8A365]"
                data-testid="total-calories"
              >
                {Math.round(profile.calories_today || 0)}
              </span>
              <span style={{ fontFamily: "Outfit, sans-serif" }} className="text-base font-bold text-white/80 mb-0.5">
                {t("daily_goal.kcal", "kcal")}
              </span>
            </div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1 font-semibold">
              {t("daily_goal.total_calories_today", "Total calories burned today")}
            </p>
          </div>
          {/* inline games / focus so the footer stays short */}
          <div className="text-right shrink-0">
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg font-bold leading-none" data-testid="total-games">{profile.games_today || 0}</p>
            <p className="text-[9px] uppercase tracking-wide text-white/50 mt-0.5">{t("daily_goal.games_played")}</p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg font-bold leading-none mt-1.5" data-testid="play-time-today">
              {(() => { const s = Math.round(profile.play_seconds_today || 0); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; })()}
            </p>
            <p className="text-[9px] uppercase tracking-wide text-white/50 mt-0.5">{t("daily_goal.played_today", "Played today")}</p>
          </div>
        </div>
      </div>

      <Link
        to="/rewards"
        data-testid="dashboard-rewards-card"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />

      <ShareCard />

      <MockAdDialog
        open={energyAd}
        onOpenChange={setEnergyAd}
        title={t("dashboard.energy_dialog_title")}
        description={t("daily_goal.refill_ad_desc")}
        rewardLabel={t("daily_goal.refill_ad_reward")}
        rewardType="energy"
        onReward={refillEnergy}
      />
      <MockAdDialog
        open={streakAd}
        onOpenChange={setStreakAd}
        title={t("daily_goal.restore_ad_title")}
        description={t("daily_goal.restore_ad_desc")}
        rewardLabel={t("daily_goal.restore_ad_reward")}
        rewardType="streak"
        onReward={restoreStreak}
      />

      <WelcomeCarousel open={showWelcome} onClose={() => setShowWelcome(false)} />

      {/* Sequential perk celebrations — only the queue head renders, so cards
          appear one after another instead of stacking. */}
      <PerkModal
        perk={perkQueue[0] || null}
        remaining={perkQueue.length}
        onNext={() => setPerkQueue((q) => q.slice(1))}
      />

      <WorldUnlockCelebration
        open={!!syncUnlock}
        world={syncUnlock}
        onDone={() => setSyncUnlock(null)}
      />

      <DailyGoalClaimCelebration
        open={!!goalClaim}
        coins={goalClaim?.coins || 0}
        boosters={goalClaim?.boosters || {}}
        onDone={() => { setGoalClaim(null); setCoinBurst((n) => n + 1); }}
      />

      <CoinBurst fire={coinBurst} />

      <Dialog open={energyDialog} onOpenChange={setEnergyDialog}>
        <DialogContent
          data-testid="energy-refill-dialog"
          className="rounded-3xl border-[#E6E2D8] max-w-[360px] sm:max-w-md p-5"
        >
          <DialogHeader className="space-y-1.5">
            <DialogTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-xl font-light flex items-center gap-2"
            >
              <Zap size={20} strokeWidth={1.5} className="text-[#5F7161]" />
              {t("dashboard.energy_dialog_title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#2C332A]/70">
              {t("daily_goal.energy_dialog_balance", { coins: profile.coins, current: profile.energy, max: profile.max_energy })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5 mt-2">
            {isHealthSupported() && stepsOn && (
              <Button
                data-testid="energy-refill-sync-steps-btn"
                disabled={syncingSteps}
                onClick={() => { setEnergyDialog(false); syncSteps(); }}
                variant="outline"
                className="w-full justify-between h-12 rounded-2xl border-[#E6E2D8] bg-[#F1EEE6] hover:bg-[#E9E5D9]"
              >
                <span className="flex items-center gap-2">
                  <Footprints size={16} strokeWidth={1.5} className="text-[#5F7161]" />
                  {syncingSteps ? t("dashboard.syncing") : t("dashboard.sync_steps_health")}
                </span>
                <span className="text-sm font-medium text-[#5F7161]">{t("dashboard.energy_steps_short", "from steps")}</span>
              </Button>
            )}
            <Button
              data-testid="energy-refill-ad-btn"
              onClick={() => { setEnergyDialog(false); setEnergyAd(true); }}
              variant="outline"
              className="w-full justify-between h-12 rounded-2xl border-[#E6E2D8] bg-white hover:bg-[#F7F5F0]"
            >
              <span className="flex items-center gap-2"><PlayCircle size={16} strokeWidth={1.5} className="text-[#5F7161]" /> {t("daily_goal.watch_ad")}</span>
              <span className="text-sm font-medium text-[#5F7161]">{t("daily_goal.plus_n_energy", { n: 1 })}</span>
            </Button>
            <Button
              data-testid="energy-refill-100-btn"
              disabled={coinRefillPending || profile.coins < 100}
              onClick={() => refillEnergyWithCoins(1)}
              className="w-full justify-between h-12 rounded-2xl bg-[#EFF1EC] hover:bg-[#E3E7DE] text-[#2C332A] border border-[#E6E2D8]"
            >
              <span className="flex items-center gap-2"><Coins size={16} strokeWidth={1.5} className="text-[#B68A3C]" /> {t("daily_goal.spend_100")}</span>
              <span className="text-sm font-medium text-[#5F7161]">{t("daily_goal.plus_n_energy", { n: 1 })}</span>
            </Button>
            <Button
              data-testid="energy-refill-450-btn"
              disabled={coinRefillPending || profile.coins < 450}
              onClick={() => refillEnergyWithCoins(5)}
              className="w-full justify-between h-12 rounded-2xl bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <span className="flex items-center gap-2"><Coins size={16} strokeWidth={1.5} /> {t("daily_goal.spend_450")}</span>
              <span className="text-sm font-medium">{t("daily_goal.plus_n_energy", { n: 5 })}</span>
            </Button>
          </div>
          <p className="text-[11px] text-[#2C332A]/50 mt-3 text-center">
            {t("daily_goal.energy_regen_note")}
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={streakDialog} onOpenChange={setStreakDialog}>
        <DialogContent
          data-testid="streak-keep-dialog"
          className="rounded-3xl border-[#E6E2D8] max-w-[360px] sm:max-w-md p-5"
        >
          <DialogHeader className="space-y-1.5">
            <DialogTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-xl font-light flex items-center gap-2"
            >
              <Flame size={20} strokeWidth={1.5} className="text-[#D0A98F]" />
              {t("dashboard.streak_dialog_title", "Keep your streak")}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#2C332A]/70">
              {t("dashboard.streak_dialog_balance", {
                coins: profile.coins,
                n: profile.current_streak,
                defaultValue: "You have {{coins}} coins · {{n}}-day streak",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5 mt-2">
            <Button
              data-testid="streak-keep-ad-btn"
              onClick={() => { setStreakDialog(false); setStreakAd(true); }}
              variant="outline"
              className="w-full justify-between h-12 rounded-2xl border-[#E6E2D8] bg-white hover:bg-[#F7F5F0]"
            >
              <span className="flex items-center gap-2"><PlayCircle size={16} strokeWidth={1.5} className="text-[#5F7161]" /> {t("daily_goal.watch_ad")}</span>
              <span className="text-sm font-medium text-[#5F7161]">{t("dashboard.streak_keep_reward", "Keep streak")}</span>
            </Button>
            <Button
              data-testid="streak-keep-coins-btn"
              disabled={streakCoinPending || profile.coins < 150}
              onClick={restoreStreakWithCoins}
              className="w-full justify-between h-12 rounded-2xl bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <span className="flex items-center gap-2"><Coins size={16} strokeWidth={1.5} /> {t("dashboard.streak_keep_coins", "Spend {{coins}} coins", { coins: 150 })}</span>
              <span className="text-sm font-medium">{t("dashboard.streak_keep_reward", "Keep streak")}</span>
            </Button>
          </div>
          <p className="text-[11px] text-[#2C332A]/50 mt-3 text-center">
            {t("dashboard.streak_dialog_note", "Keep your streak alive for today without losing your progress.")}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
